import { useEffect, useState, useMemo } from "react";
import { MapPin } from "lucide-react";
import { api } from "../lib/api";
import SectionHeader from "../components/SectionHeader";
import Seo from "../components/Seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function Projects() {
    const [projects, setProjects] = useState([]);
    const [filter, setFilter] = useState("All");

    useEffect(() => {
        api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    }, []);

    const categories = useMemo(() => {
        const set = new Set(projects.map((p) => p.category));
        return ["All", ...set];
    }, [projects]);

    const filtered = filter === "All" ? projects : projects.filter((p) => p.category === filter);

    return (
        <div data-testid="projects-page">
            <Seo
                title="Our Projects — Extensions, Lofts & Renovations in Berkshire"
                description="Recent builds across Wokingham, Reading, Henley, Bracknell and surrounding Berkshire areas. Real homes, real craftsmanship."
                path="/projects"
            />
            <section className="bg-muted border-b border-border">
                <div className="container-x py-20 md:py-28">
                    <SectionHeader
                        eyebrow="Our Projects"
                        title="Real homes. Real craftsmanship."
                        lead="A look at recent builds across Berkshire — from rear extensions and loft conversions to full-house renovations and groundworks."
                    />
                </div>
            </section>

            <section className="section-pad container-x">
                <div className="flex flex-wrap gap-2" data-testid="project-filters">
                    {categories.map((c) => (
                        <button
                            key={c}
                            onClick={() => setFilter(c)}
                            data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-colors ${
                                filter === c
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-transparent text-foreground border-border hover:border-primary"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((p) => (
                        <article
                            key={p.id}
                            className="group rounded-lg overflow-hidden bg-card border border-border hover:shadow-md transition-all"
                            data-testid={`project-${p.id}`}
                        >
                            <div className="aspect-[4/3] overflow-hidden">
                                <img
                                    src={resolveImg(p.image)}
                                    alt={p.title}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                            </div>
                            <div className="p-6">
                                <div className="text-xs uppercase tracking-[0.2em] text-secondary font-medium">
                                    {p.category}
                                </div>
                                <h3 className="mt-2 text-xl font-heading font-bold tracking-tight">
                                    {p.title}
                                </h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                    {p.description}
                                </p>
                                <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <MapPin size={13} /> {p.location}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
                {filtered.length === 0 && (
                    <p className="text-center py-20 text-muted-foreground">No projects in this category yet.</p>
                )}
            </section>
        </div>
    );
}
